import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const AccountContext = createContext(null);

const emptySupport = {
    phone: '',
    email: '',
    instagram: 'https://www.instagram.com/dnystu/',
    instagramHandle: '@dnystu',
};

export const AccountProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [orders, setOrders] = useState([]);
    const [support, setSupport] = useState(emptySupport);
    const [checking, setChecking] = useState(true);
    const [error, setError] = useState('');
    const [capabilities, setCapabilities] = useState({});

    const refresh = useCallback(async () => {
        try {
            const response = await fetch('/api/account', { credentials: 'include' });
            if (!response.ok) throw new Error('Your account could not be loaded. Please try again.');
            const data = await response.json();
            setError('');
            setUser(data.user || null);
            setOrders(Array.isArray(data.orders) ? data.orders : []);
            setSupport(data.support || emptySupport);
            setCapabilities(data.capabilities || {});
            return data;
        } catch (error) {
            setError('Your account could not be loaded. Please check your connection and try again.');
            setUser(null);
            setOrders([]);
            return { user: null };
        } finally {
            setChecking(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const post = async (url, body) => {
        const response = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body || {}),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(data.error || 'Something went wrong');
        }
        return data;
    };

    const signup = async (body) => {
        const data = await post('/api/account/signup', body);
        await refresh();
        return data;
    };

    const login = async (body) => {
        const data = await post('/api/account/login', body);
        await refresh();
        return data;
    };

    const logout = async () => {
        await post('/api/account/logout');
        setUser(null);
        setOrders([]);
    };

    const saveProfile = async (body) => {
        const response = await fetch('/api/account', {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(data.error || 'Could not save');
        }
        await refresh();
        return data;
    };

    const openStripePortal = async () => {
        const data = await post('/api/account/portal');
        if (data.url) {
            window.location.assign(data.url);
        }
        return data;
    };

    return (
        <AccountContext.Provider
            value={{
                user,
                orders,
                support,
                checking,
                error,
                capabilities,
                refresh,
                signup,
                login,
                logout,
                saveProfile,
                openStripePortal,
            }}
        >
            {children}
        </AccountContext.Provider>
    );
};

export const useAccount = () => {
    const context = useContext(AccountContext);
    if (!context) {
        throw new Error('useAccount must be used within AccountProvider');
    }
    return context;
};
